import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import {
    Box,
    Chip,
    InputAdornment,
    List,
    ListItemButton,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { OfferConfig } from '../types';
import { getLanguageVariantCount, hasLanguageRequiredFieldErrors, isLanguageDirty } from '../utils';

type LanguageSidebarProps = {
    offer: OfferConfig;
    snapshotOffer: OfferConfig | null;
    selectedLanguageIndex: number;
    canEdit: boolean;
    searchText: string;
    onSearchChange: (text: string) => void;
    onSelectLanguage: (index: number) => void;
};

export default function LanguageSidebar({
    offer,
    snapshotOffer,
    selectedLanguageIndex,
    canEdit,
    searchText,
    onSearchChange,
    onSelectLanguage,
}: LanguageSidebarProps) {
    const filteredLanguages = offer.languages
        .map((lang, index) => ({ lang, index }))
        .filter(({ lang }) => lang.language_name.toLowerCase().includes(searchText.trim().toLowerCase()));

    return (
        <Box className="oz-offer-language-config-panel oz-offer-language-config-sidebar">
            <div className="oz-offer-language-config-sidebar-header">
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ marginBottom: '8px' }}>
                    <Typography sx={{ color: '#0f172a', fontSize: '16px', fontWeight: 500 }}>
                        Languages ({offer.languages.length})
                    </Typography>
                    {!canEdit && <Chip size="small" label="View only" variant="outlined" />}
                </Stack>

                <TextField
                    value={searchText}
                    onChange={(event) => onSearchChange(event.target.value)}
                    placeholder="Search language"
                    size="small"
                    fullWidth
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchOutlinedIcon sx={{ color: '#94a3b8' }} fontSize="small" />
                            </InputAdornment>
                        ),
                    }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                />
            </div>

            <div className="oz-offer-language-config-sidebar-body">
                {filteredLanguages.length === 0 ? (
                    <div className="oz-offer-language-config-empty">
                        <Typography sx={{ fontSize: '13px', lineHeight: '18px' }}>
                            No languages match the search.
                        </Typography>
                    </div>
                ) : (
                    <List disablePadding>
                        {filteredLanguages.map(({ lang, index }) => {
                            const languageHasError = hasLanguageRequiredFieldErrors(lang);
                            const languageDirty = isLanguageDirty(offer, snapshotOffer, index);

                            return (
                                <ListItemButton
                                    key={lang.language_name}
                                    className={`oz-offer-language-config-language-button ${selectedLanguageIndex === index ? 'is-active' : ''}`}
                                    onClick={() => onSelectLanguage(index)}
                                >
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography className="oz-offer-language-config-language-title">
                                            {lang.language_name}
                                        </Typography>
                                        <Typography className="oz-offer-language-config-language-subtitle">
                                            {getLanguageVariantCount(lang)} variants
                                        </Typography>
                                    </Box>
                                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ marginLeft: '12px' }} alignItems="center">
                                        {languageDirty && <Chip size="small" label="Edited" color="warning" variant="outlined" />}
                                        {languageHasError && <Chip size="small" label="Fields missing" color="error" variant="outlined" />}
                                    </Stack>
                                </ListItemButton>
                            );
                        })}
                    </List>
                )}
            </div>
        </Box>
    );
}
